import { Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { useMemo } from 'react';
import type { Graph3DNodeRendererProps } from '../../core/renderer.types';
import { createRoundedRectShape, getNodeDataString } from './nodeShapeUtils';
import { NodeTextHitPlane } from './NodeTextHitPlane';

const HTML_CONTENT_Z_INDEX_RANGE: [number, number] = [160, 0];

export function OrgUnitNode3D({
  node,
  runtime,
  theme,
  hovered,
  selected,
  focused,
  onHover,
  onSelect,
  onFocus,
}: Graph3DNodeRendererProps) {
  const status = runtime?.status ?? 'idle';
  const active =
    runtime?.active || runtime?.highlighted || hovered || selected || focused;
  const themeColor = getNodeDataString(
    node.data,
    'themeColor',
    theme.runtime[status] ?? theme.node.border
  );
  const isCompany = node.type === 'company';
  const companyFaceFill = '#314158';
  const companySideFill = '#22324a';
  const fill = !active ? '#e5e7eb' : isCompany ? companySideFill : themeColor;
  const textColor = !active ? '#9ca3af' : '#ffffff';
  const opacity = runtime?.hidden ? 0.16 : 1;
  const shape = useMemo(
    () =>
      createRoundedRectShape(
        node.resolvedSize.width,
        node.resolvedSize.height,
        isCompany ? 28 : 20
      ),
    [isCompany, node.resolvedSize.height, node.resolvedSize.width]
  );
  const extrudeSettings = useMemo(
    () => ({ depth: node.resolvedSize.depth, bevelEnabled: false }),
    [node.resolvedSize.depth]
  );
  const subtitle = getNodeDataString(node.data, 'subtitle');
  const labelLines = node.label.split('\n').filter(Boolean);
  const titleFontSize = isCompany ? 17 : 14;
  const titleLineHeight = isCompany ? '20px' : '18px';

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(node.id);
    onFocus(node.id);
  };

  return (
    <group
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      onClick={handleClick}
      onPointerEnter={event => {
        event.stopPropagation();
        onHover(node.id);
      }}
      onPointerLeave={event => {
        event.stopPropagation();
        onHover(null);
      }}
    >
      <mesh position={[0, 0, -node.resolvedSize.depth / 2]}>
        <extrudeGeometry args={[shape, extrudeSettings]} />
        <meshStandardMaterial
          color={fill}
          emissive={isCompany ? companySideFill : fill}
          emissiveIntensity={isCompany ? 0.02 : active ? 0.16 : 0.04}
          roughness={isCompany ? 0.5 : 0.42}
          metalness={0.02}
          transparent
          opacity={opacity}
        />
      </mesh>
      <mesh position={[0, 0, node.resolvedSize.depth / 2 + 0.7]}>
        <extrudeGeometry args={[shape, { depth: 0.8, bevelEnabled: false }]} />
        <meshBasicMaterial
          color={
            isCompany && active ? companyFaceFill : active ? fill : '#cbd5e1'
          }
          transparent
          opacity={isCompany && active ? 0.96 : active ? 0.32 : 0.2}
        />
      </mesh>
      <NodeTextHitPlane
        width={node.resolvedSize.width}
        height={node.resolvedSize.height}
        z={node.resolvedSize.depth / 2 + 7}
      />
      <Html
        center
        transform
        distanceFactor={400}
        zIndexRange={HTML_CONTENT_Z_INDEX_RANGE}
        position={[0, 0, node.resolvedSize.depth / 2 + 7]}
      >
        <div
          style={{
            width: node.resolvedSize.width - 28,
            color: textColor,
            fontFamily:
              'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
            textAlign: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          <div
            style={{
              fontSize: titleFontSize,
              fontWeight: 800,
              lineHeight: titleLineHeight,
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: isCompany ? 2 : 3,
            }}
          >
            {labelLines.join('\n') || node.label}
          </div>
          {(labelLines[1] || subtitle) && (
            <div
              style={{
                marginTop: 3,
                color: active ? 'rgba(255,255,255,.86)' : '#98a2b3',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: '13px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {labelLines[1] || subtitle}
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}
